// Export all types
export * from './notifications'
export * from './achievements'

// Application types
export type AgeCategory = 'Adult' | 'Teen' | 'Child'
export type Priority = 'High' | 'Medium' | 'Low'
export type TaskStatus = 'upcoming' | 'overdue' | 'completed' | 'skipped'

export interface FamilyMember {
  id: string
  name: string
  avatar_url: string | null
  color: string
  age_category: AgeCategory
  notification_sound: string
  points: number
  streak: number
  created_at: string
  updated_at: string
}

export interface Task {
  id: string
  title: string
  room: string
  task_type: string
  assigned_to: string
  priority: Priority
  estimated_duration: number
  notes: string | null
  recurrence_rule: string | null
  start_date: string
  end_date: string | null
  skip_dates: string[]
  created_at: string
  updated_at: string
  supplies?: Supply[]
}

export interface TaskCompletion {
  id: string
  task_id: string
  completed_by: string
  completed_at: string
  time_taken: number | null
  on_time: boolean
  notes: string | null
  created_at: string
  task?: Task
  family_member?: FamilyMember
}

export interface Supply {
  id: string
  name: string
  category: string
  quantity: number
  min_quantity: number
  cost: number | null
  purchase_link: string | null
  created_at: string
  updated_at: string
}

export interface TaskWithDetails extends Task {
  family_member?: FamilyMember
  supplies?: Supply[]
  completions?: TaskCompletion[]
}

// UI-specific types
export interface CalendarEvent {
  id: string
  title: string
  start: Date
  end: Date
  resource: Task
}

// Form types
export interface TaskFormData {
  title: string
  room: string
  task_type: string
  assigned_to: string
  priority: Priority
  estimated_duration: number
  notes: string
  recurrence_rule: string | null
  start_date: string
  end_date: string | null
  skip_dates: string[]
  supply_ids: string[]
}

export interface FamilyMemberFormData {
  name: string
  avatar_url: string | null
  color: string
  age_category: AgeCategory
  notification_sound: string
}

export interface SupplyFormData {
  name: string
  category: string
  quantity: number
  min_quantity: number
  cost: number | null
  purchase_link: string | null
}

// Constants
export const ROOMS = [
  'Kitchen',
  'Bathroom',
  'Bedroom',
  'Living Room',
  'Dining Room',
  'Office',
  'Garage',
  'Outdoor',
  'Other',
] as const

export const TASK_TYPES = [
  'Deep Clean',
  'Quick Tidy',
  'Vacuum',
  'Mop',
  'Dust',
  'Organize',
  'Sanitize',
  'Windows',
  'Laundry',
  'Other',
] as const

export const SUPPLY_CATEGORIES = [
  'Chemicals',
  'Tools',
  'Disposables',
  'Equipment',
  'Other',
] as const

export const RECURRENCE_PRESETS = [
  { label: 'Daily', value: 'FREQ=DAILY' },
  { label: 'Weekly', value: 'FREQ=WEEKLY' },
  { label: 'Bi-weekly', value: 'FREQ=WEEKLY;INTERVAL=2' },
  { label: 'Monthly', value: 'FREQ=MONTHLY' },
  { label: 'Quarterly', value: 'FREQ=MONTHLY;INTERVAL=3' },
  { label: 'Annually', value: 'FREQ=YEARLY' },
] as const

export const DEFAULT_COLORS = [
  '#1976d2', // Blue
  '#dc004e', // Pink
  '#9c27b0', // Purple
  '#f57c00', // Orange
  '#388e3c', // Green
  '#d32f2f', // Red
  '#0097a7', // Cyan
  '#7b1fa2', // Deep Purple
] as const
